import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import GalleryLightbox from '../components/GalleryLightbox'

export default function ProjectDetail() {
  const { slug } = useParams()
  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [lightboxIndex, setLightboxIndex] = useState(null)

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/v2/content/projekter')
      .then((res) => res.json())
      .then((data) => {
        if (data && data.items) {
          const found = data.items.find((item) => item.url_slug === slug || item.id === slug)
          if (found) setProject(found)
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [slug])

  if (loading) {
    return (
      <div className="py-32 text-center text-zinc-500 font-mono text-xs uppercase bg-zinc-950 min-h-[60vh]">
        Indlæser projekt...
      </div>
    )
  }

  if (!project) {
    return (
      <div className="py-32 text-center bg-zinc-950 min-h-[60vh]">
        <h1 className="text-2xl font-display font-bold text-white uppercase">Projektet blev ikke fundet</h1>
        <p className="text-zinc-400 text-xs mt-2">Det søgte projekt eksisterer ikke eller er blevet flyttet.</p>
        <Link to="/projekter" className="mt-6 inline-block px-6 py-3 bg-orange-500 text-zinc-950 font-display font-bold text-xs uppercase tracking-widest border border-orange-500">
          Tilbage til alle projekter
        </Link>
      </div>
    )
  }

  const mainImage = Array.isArray(project.hovedbillede) && project.hovedbillede[0]
    ? project.hovedbillede[0]
    : 'https://assets.auxodev.dk/assets/bac61bd8-01e8-4025-a9be-38664430d08c/agstner-hero-carpentry.jpg'

  return (
    <>
      <SEOHead
        title={`${project.projektnavn} - Agstner Byg ApS`}
        description={project.kort_beskrivelse || `Se detaljer om projektet ${project.projektnavn} udført af Agstner Byg ApS.`}
      />

      {/* Header */}
      <section className="relative py-16 bg-zinc-950 overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0 z-0">
          <img
            src={mainImage}
            alt={project.projektnavn}
            className="w-full h-full object-cover opacity-65"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/85 via-zinc-950/50 to-zinc-950/20" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/projekter" className="inline-flex items-center gap-2 text-xs font-display font-bold uppercase tracking-widest text-orange-500 hover:underline mb-6">
            <i className="fa-solid fa-arrow-left text-[10px]" aria-hidden="true"></i>
            <span>Tilbage til alle projekter</span>
          </Link>

          <div className="flex flex-wrap items-center gap-3 mb-2">
            <span className="px-3 py-1 bg-zinc-900 border border-zinc-800 text-orange-500 text-xs font-mono font-bold uppercase">
              {project.projektkategori}
            </span>
            {project.omraade && (
              <span className="text-xs text-zinc-400 font-mono uppercase flex items-center gap-1">
                <i className="fa-solid fa-location-dot text-orange-500" aria-hidden="true"></i>
                {project.omraade}
              </span>
            )}
          </div>

          <h1 className="text-3xl sm:text-5xl font-display font-bold text-white uppercase tracking-tight">
            {project.projektnavn}
          </h1>
          {project.kort_beskrivelse && (
            <p className="mt-3 text-sm sm:text-base text-zinc-300 max-w-3xl leading-relaxed">
              {project.kort_beskrivelse}
            </p>
          )}
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-zinc-900 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Left Col: Main Image & Details */}
            <div className="lg:col-span-8 space-y-8">
              <div
                onClick={() => setLightboxIndex(0)}
                className="relative border border-zinc-800 bg-zinc-950 p-0 cursor-pointer group"
              >
                <img
                  src={mainImage}
                  alt={project.projektnavn}
                  className="w-full h-[450px] object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-zinc-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                  <span className="bg-zinc-950 border border-zinc-800 text-orange-500 px-4 py-2 font-display text-xs uppercase font-bold tracking-widest flex items-center gap-2">
                    <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true"></i>
                    <span>Åbn billede i stor visning</span>
                  </span>
                </div>
              </div>

              {/* Long Description */}
              {project.lang_beskrivelse && (
                <div className="bg-zinc-950 p-6 sm:p-8 border border-zinc-800 space-y-4">
                  <h2 className="text-xl font-display font-bold text-white uppercase">Om projektet</h2>
                  <div
                    className="prose prose-invert max-w-none text-zinc-300 text-xs sm:text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: project.lang_beskrivelse }}
                  />
                </div>
              )}
            </div>

            {/* Right Col: Performed Tasks & Specs */}
            <div className="lg:col-span-4 space-y-6">
              
              {/* Box 1: Udførte opgaver */}
              {project.udforte_opgaver && (
                <div className="bg-zinc-950 p-6 border border-zinc-800 space-y-3">
                  <h3 className="text-xs font-display font-bold uppercase tracking-widest text-orange-500 flex items-center gap-2">
                    <i className="fa-solid fa-list-check" aria-hidden="true"></i>
                    <span>Udførte arbejdsopgaver</span>
                  </h3>
                  <p className="text-xs text-zinc-300 leading-relaxed font-mono">
                    {project.udforte_opgaver}
                  </p>
                </div>
              )}

              {/* Box 2: Anvendte materialer */}
              {project.anvendte_materialer && (
                <div className="bg-zinc-950 p-6 border border-zinc-800 space-y-3">
                  <h3 className="text-xs font-display font-bold uppercase tracking-widest text-orange-500 flex items-center gap-2">
                    <i className="fa-solid fa-cubes" aria-hidden="true"></i>
                    <span>Anvendte materialer</span>
                  </h3>
                  <p className="text-xs text-zinc-300 leading-relaxed font-mono">
                    {project.anvendte_materialer}
                  </p>
                </div>
              )}

              {/* Box 3: Quick Offer CTA */}
              <div className="bg-zinc-950 p-6 border border-zinc-800 text-center space-y-4">
                <h3 className="text-lg font-display font-bold text-white uppercase">Ønsker du et lignende projekt?</h3>
                <p className="text-xs text-zinc-400">
                  Kontakt Agstner Byg ApS for en uforpligtende drøftelse og et tilbud på din opgave.
                </p>
                <a
                  href="/kontakt"
                  className="block w-full py-3.5 px-4 bg-orange-500 hover:bg-orange-600 text-zinc-950 font-display font-bold text-xs uppercase tracking-widest transition-colors border border-orange-500"
                >
                  Få et tilbud
                </a>
              </div>

            </div>

          </div>
        </div>
      </section>

      {lightboxIndex !== null && (
        <GalleryLightbox
          images={[mainImage]}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  )
}
